import Component from '@glimmer/component';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type { ComponentLike } from '@glint/template';
import { eq } from 'tio-ui/utilities';

// ---------------------------------------------------------------------------
// FilterTabs – horizontal pill-style filter buttons with active state
// ---------------------------------------------------------------------------

export interface FilterTab {
  id: string;
  label: string;
  count?: number;
  icon?: ComponentLike<{ Element: HTMLElement; Args: { class?: string } }>;
}

interface FilterTabsSignature {
  Args: {
    /** Array of tab definitions */
    tabs: FilterTab[];
    /** ID of the currently active tab */
    activeId?: string;
    /** Callback when a tab is selected */
    onSelect?: (id: string) => void;
    /** Custom class for the active tab pill */
    activeClass?: string;
    /** Custom class for inactive tab pills */
    inactiveClass?: string;
    /** Custom class for count badge on active tab */
    countActiveClass?: string;
    /** Custom class for count badge on inactive tabs */
    countInactiveClass?: string;
    /** Whether to show count badges (default: true) */
    showCounts?: boolean;
  };
  Element: HTMLDivElement;
}

export default class FilterTabs extends Component<FilterTabsSignature> {
  get activeClass() {
    return this.args.activeClass ?? 'bg-violet-600 text-white shadow-md';
  }

  get inactiveClass() {
    return (
      this.args.inactiveClass ?? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
    );
  }

  get countActiveClass() {
    return this.args.countActiveClass ?? 'bg-violet-500';
  }

  get countInactiveClass() {
    return this.args.countInactiveClass ?? 'bg-gray-200';
  }

  get showCounts() {
    return this.args.showCounts !== false;
  }

  handleSelect = (id: string) => {
    this.args.onSelect?.(id);
  };

  <template>
    <div class="flex flex-wrap gap-2" ...attributes>
      {{#each @tabs as |tab|}}
        <button
          type="button"
          class="px-4 py-2 rounded-full text-sm font-medium transition-all
            {{if (eq @activeId tab.id) this.activeClass this.inactiveClass}}"
          {{on "click" (fn this.handleSelect tab.id)}}
        >
          {{#if tab.icon}}
            {{#let tab.icon as |Icon|}}
              <Icon class="mr-2 inline-flex" />
            {{/let}}
          {{/if}}
          {{tab.label}}
          {{#if this.showCounts}}
            {{#if tab.count}}
              <span
                class="ml-1.5 px-1.5 py-0.5 rounded-full text-xs
                  {{if (eq @activeId tab.id) this.countActiveClass this.countInactiveClass}}"
              >
                {{tab.count}}
              </span>
            {{/if}}
          {{/if}}
        </button>
      {{/each}}
    </div>
  </template>
}
